/**
 * Labstep
 *
 * @module models/resource
 * @desc Typescript export class for Resource
 */

import { Type } from 'class-transformer';
import type { PermissionActions } from 'labstep-web/models';
import { CustomIdentifierSet } from 'labstep-web/models/custom-identifier-set.model';
import { Entity } from 'labstep-web/models/entity.model';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import { EntityUser } from 'labstep-web/models/entity-user.model';
import type { FieldDefinition } from 'labstep-web/models/field-definition.model';
import { File as FileModel } from 'labstep-web/models/file.model';
import { Group } from 'labstep-web/models/group.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Molecule } from 'labstep-web/models/molecule.model';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import { PermaLink } from 'labstep-web/models/perma-link.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type { ResourceLocation } from 'labstep-web/models/resource-location.model';
import { ShareLink } from 'labstep-web/models/share-link.model';
import { Tag } from 'labstep-web/models/tag.model';
import { Thread } from 'labstep-web/models/thread.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';

export type ResourceEntityNameType = 'resource';

export class Resource extends Entity {
  public static readonly entityName: ResourceEntityNameType =
    'resource';

  public static readonly templateEntityName = 'resource_template';

  public readonly defaultImageSrc =
    '/img/resource-category/default.svg';

  public get entityName(): typeof Resource.entityName {
    return Resource.entityName;
  }

  public constructor(data: Partial<Resource> = {}) {
    super();
    Object.assign(this, data);
  }

  public id!: number;

  public name!: string;

  public is_template!: boolean;

  @Type(() => Group)
  public owner!: Group;

  public resource_item_count!: number;

  public available_resource_item_count!: number;

  public available_resource_item_count_alert_threshold!:
    | number
    | null;

  public entity_users_count!: number;

  /** Present only while searching for molecule structure */
  public molecule_structure?: string[];

  public metadata_molecule_svg!: string[];

  public allowed_actions!: PermissionActions[];

  public custom_identifier!: string;

  // For Typescript API payload shape
  public group!: Group;

  @Type(() => Molecule)
  public molecule?: Molecule;

  @Type(() => ResourceItem)
  public resource_item_template?: ResourceItem;

  @Type(() => OrderRequest)
  public order_request_template?: OrderRequest;

  @Type(() => OrderRequest)
  public order_request?: OrderRequest;

  @Type(() => FileModel)
  public image?: FileModel;

  @Type(() => User)
  public author!: User;

  @Type(() => ProtocolValue)
  public protocol_value_origin?: ProtocolValue;

  @Type(() => Thread)
  public thread?: Thread;

  @Type(() => MetadataThread)
  public metadata_thread!: MetadataThread;

  @Type(() => PermaLink)
  public perma_link!: PermaLink;

  @Type(() => Resource)
  public template!: Resource | null;

  @Type(() => EntityUser)
  public entity_users_preview!: EntityUser[];

  @Type(() => CustomIdentifierSet)
  public _custom_identifier_set?: CustomIdentifierSet;

  @Type(() => EntityRelation)
  public entity_relations!: EntityRelation[];

  @Type(() => Tag)
  public tags!: Tag[];

  @Type(() => ShareLink)
  public share_link!: ShareLink;

  public getEntityRelationsByFieldDefinition(
    fieldDefinition: FieldDefinition,
  ) {
    if (!fieldDefinition) {
      return [];
    }
    if (
      fieldDefinition.entity_relation_type ===
      EntityRelation.TYPE_ONE_WAY
    ) {
      return this.entity_relations.filter((entityRelation) => {
        return (
          entityRelation &&
          entityRelation.deleted_at === null &&
          entityRelation.field_definition.id === fieldDefinition.id &&
          (!entityRelation.source ||
            entityRelation.source.id === this.id)
        );
      });
    }
    if (
      fieldDefinition.entity_relation_type ===
      EntityRelation.TYPE_SYMMETRIC
    ) {
      return this.entity_relations.filter((entityRelation) => {
        return (
          entityRelation &&
          entityRelation.deleted_at === null &&
          entityRelation.field_definition.id === fieldDefinition.id &&
          (!entityRelation.source ||
            entityRelation.source.id === this.id ||
            !entityRelation.target ||
            entityRelation.target.id === this.id)
        );
      });
    }

    if (
      fieldDefinition.entity_relation_type ===
      EntityRelation.TYPE_ASYMMETRIC
    ) {
      const entity_relations = this.entity_relations.filter(
        (entityRelation) => {
          return (
            entityRelation &&
            entityRelation.deleted_at === null &&
            entityRelation.field_definition.id ===
              fieldDefinition.id &&
            (!entityRelation.source ||
              entityRelation.source.id === this.id ||
              !entityRelation.target ||
              entityRelation.target.id === this.id)
          );
        },
      );

      if (entity_relations.length > 0) {
        return entity_relations;
      }

      const inverse_relations = this.entity_relations.filter(
        (entityRelation) => {
          return (
            entityRelation &&
            entityRelation.deleted_at === null &&
            entityRelation.field_definition.id ===
              fieldDefinition.entity_relation_inverse_field_definition
                ?.id &&
            (!entityRelation.source ||
              entityRelation.source.id === this.id ||
              !entityRelation.target ||
              entityRelation.target.id === this.id)
          );
        },
      );
      return inverse_relations;
    }
    return [];
  }

  public get displayName(): string {
    if (this.custom_identifier) {
      return this.name
        ? `${this.custom_identifier} - ${this.name}`
        : this.custom_identifier;
    }
    return (
      this.display_name ||
      this.name ||
      `Untitled ${getHumanReadableEntityName(
        this.entityName,
        false,
        true,
      )}`
    );
  }

  public get displayNameWithoutIdentifier(): string {
    return (
      this.name ||
      `Untitled ${getHumanReadableEntityName(
        this.entityName,
        false,
        true,
      )}`
    );
  }

  public get custom_identifier_set(): CustomIdentifierSet | null {
    return this._custom_identifier_set?.deleted_at ||
      !this._custom_identifier_set
      ? null
      : this._custom_identifier_set;
  }

  public set custom_identifier_set(customIdentifierSet: CustomIdentifierSet) {
    this._custom_identifier_set = customIdentifierSet;
  }

  public get hasNonDeletedResourceItemTemplate(): boolean {
    return (
      !!this.resource_item_template &&
      !this.resource_item_template.deleted_at
    );
  }

  public get nonDeletedTemplate(): Resource | null {
    if (this.template && !this.template.deleted_at) {
      return this.template;
    }
    return null;
  }

  public get hasNonDeletedOrderRequestTemplate(): boolean {
    return (
      !!this.order_request_template &&
      !this.order_request_template.deleted_at
    );
  }

  public get hasTemplateWithNonDeletedResourceItemTemplate(): boolean {
    return (
      !!this.template?.resource_item_template &&
      !this.template.resource_item_template.deleted_at
    );
  }

  public get templateMetadatas(): Metadata[] {
    if (this.hasNonDeletedResourceItemTemplate) {
      return this.resource_item_template!.metadata_thread.metadatas;
    }

    if (this.hasTemplateWithNonDeletedResourceItemTemplate) {
      return this.template!.resource_item_template!.metadata_thread
        .metadatas;
    }

    return [];
  }

  /**
   * Checks if the resource has any metadatas that are required
   * but do not have a value.
   */
  public get requiredMetadatasWithoutValue(): Metadata[] {
    const requiredMetadatas = this.metadata_thread.metadatas.filter(
      (metadata) =>
        metadata.is_required &&
        !metadata.template_id &&
        !metadata.hasValue,
    );

    if (this.nonDeletedTemplate) {
      requiredMetadatas.push(
        ...this.nonDeletedTemplate.metadata_thread.metadatas.filter(
          (metadata) =>
            metadata.is_required &&
            !this.metadata_thread.metadatas.find(
              (m) => m.template_id === metadata.id,
            )?.hasValue,
        ),
      );
    }
    return requiredMetadatas;
  }

  /**
   * The location to use for new items of this resource.
   * Returns the location of either the custom item template (if defined)
   * or the category item template (if defined)
   */
  public get itemTemplateLocation():
    | ResourceLocation
    | null
    | undefined {
    if (this.hasNonDeletedResourceItemTemplate) {
      return this.resource_item_template!.resource_location;
    }

    if (this.hasTemplateWithNonDeletedResourceItemTemplate) {
      return this.template!.resource_item_template!.resource_location;
    }

    return null;
  }

  public get templateId(): string | undefined {
    if (
      this.resource_item_template &&
      !this.resource_item_template.deleted_at
    ) {
      return this.resource_item_template.guid;
    }
    if (this.template?.resource_item_template) {
      return this.template?.resource_item_template.guid;
    }

    return undefined;
  }

  public static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
    isTemplate?: boolean,
  ): string {
    return getHumanReadableEntityName(
      this.entityName,
      plural,
      capitalized,
      isTemplate,
    );
  }
}
